import React, { useState, useEffect } from 'react';
import { 
  Search, Grid, List, RefreshCw, MapPin, Calendar,
  TrendingUp, Users, Target, CheckCircle, XCircle
} from 'lucide-react';

import './PreSowing.css';

const RosetteStage = () => {
  const API_BASE = " https://sadaatagrofarms.com/api/crop_stages/rosette-stage/";
  const token = localStorage.getItem("authToken");   // MUST HAVE TOKEN

  const [viewMode, setViewMode] = useState("grid");
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [activities, setActivities] = useState([]);

  const [stats, setStats] = useState({
    totalActivities: 0,
    landPrepared: 0,
    notPrepared: 0,
    activeFarmers: 0,
    plannedArea: 0
  });

  // ------------ FETCH API WITH BEARER TOKEN ------------
  const loadRosetteData = async () => {
    try {
      const res = await fetch(API_BASE, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`  // ADD TOKEN HERE
        }
      });

      const data = await res.json();

      const results = data.results || data;

      setActivities(results);

      setStats({
        totalActivities: results.length,
        landPrepared: results.filter(a => a.weeds_observed === false).length,
        notPrepared: results.filter(a => a.weeds_observed === true).length,
        activeFarmers: results.length,
        plannedArea: results.length * 10
      });

    } catch (err) {
      console.error("Rosette Stage API Error:", err);
    }
  };

  useEffect(() => {
    loadRosetteData();
  }, []);

  // ------------ FILTER + SEARCH LOGIC ------------
  const filteredActivities = activities
    .filter(a => {
      if (filterStatus === "Prepared") return a.weeds_observed === false;
      if (filterStatus === "Not Prepared") return a.weeds_observed === true;
      return true;
    })
    .filter(a =>
      a.crop_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.crop_height?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <div className="pre-sowing">

      <div className="stage-tabs">
        <button className="stage-tab">Pre-Sowing</button>
        <button className="stage-tab">Sowing & Emergence</button>
        <button className="stage-tab">Emergence</button>
        <button className="stage-tab">Growth & Tillering</button>
        <button className="stage-tab active">Rosette Stage</button>
      </div>

      <div className="page-header">
        <div>
          <h1>Rosette Stage Dashboard</h1>
          <p className="subtitle">Analyze field growth and rosette stage conditions</p>
        </div>
        <button className="refresh-btn" onClick={loadRosetteData}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green"><TrendingUp size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalActivities}</div>
            <div className="stat-label">Total Entries</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.landPrepared}</div>
            <div className="stat-label">Healthy</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red"><XCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value danger">{stats.notPrepared}</div>
            <div className="stat-label">Under Stress</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue"><Users size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeFarmers}</div>
            <div className="stat-label">Active Fields</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple"><Target size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.plannedArea} ha</div>
            <div className="stat-label">Area</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="controls-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search (crop, height)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="view-toggle">
          <button className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewMode("grid")}
          >
            <Grid size={18} />
          </button>

          <button className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
            onClick={() => setViewMode("list")}
          >
            <List size={18} />
          </button>
        </div>

        <div className="status-filters">
          {["All", "Prepared", "Not Prepared"].map((s) => (
            <button
              key={s}
              className={`filter-chip ${filterStatus === s ? "active" : ""}`}
              onClick={() => setFilterStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Activities */}
      <div className={`activities-${viewMode}`}>
        {filteredActivities.map((item) => (
          <div key={item.id} className="activity-card">

            <div className="activity-header">
              <div className="activity-crop">
                🌾 <span className="crop-name">{item.crop_name}</span>
              </div>

              <span className={`status-badge ${item.crop_under_stress ? "not-prepared" : "prepared"}`}>
                {item.crop_under_stress ? "Under Stress" : "Healthy"}
              </span>
            </div>

            <div className="activity-location">
              <MapPin size={14} />
              <span>Farmer {item.farmer}</span>
            </div>

            <div className="activity-details">
              <div className="detail-row">
                <span className="detail-label">Height</span>
                <span className="detail-value">{item.crop_height} cm</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Health</span>
                <span className="detail-value">{item.crop_health}</span>
              </div>
            </div>

            <div className="activity-footer">
              <Calendar size={14} />
              <strong>{new Date(item.created_at).toLocaleDateString()}</strong>
            </div>

          </div>
        ))}
      </div>
    </div>
  );
};

export default RosetteStage;
